import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/** Shared page header — matches homepage brand system (eyebrow, navy headline, orange italic accent) */
export function PageHeader({ eyebrow, title, accent, subtitle, kicker, tone = "cream", "data-testid": testId }) {
  const bg = tone === "stone" ? "bg-brand-sand" : "bg-brand-cream";
  return (
    <section className={`relative overflow-hidden ${bg}`} data-testid={testId || "page-header"}>
      <svg className="absolute -top-16 -left-16 w-[380px] h-[380px] pointer-events-none hidden md:block" viewBox="0 0 380 380" aria-hidden="true">
        <defs>
          <linearGradient id="ph-navy" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#1e2a78" stopOpacity="0.08"/>
            <stop offset="100%" stopColor="#1e2a78" stopOpacity="0"/>
          </linearGradient>
        </defs>
        <path d="M -20 200 C 60 60, 220 30, 320 100 C 400 160, 380 300, 260 340 C 140 380, 20 320, -20 240 Z" fill="url(#ph-navy)"/>
      </svg>

      <div className="wc-container relative pt-6 md:pt-10 pb-12 md:pb-16">
        <div className="inline-flex items-center gap-2.5 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.28em] text-brand-navy" data-testid={`${testId || 'page'}-eyebrow`}>
          <span className="h-px w-8 bg-brand-orange"/>
          {eyebrow}
        </div>
        <h1 className="mt-5 font-outfit font-semibold tracking-tight text-brand-navy leading-[1.06] text-4xl md:text-5xl lg:text-[3.5rem] max-w-4xl" data-testid={`${testId || 'page'}-title`}>
          {title}
          {accent && (
            <>
              {" "}
              <span className="italic font-medium text-brand-orange relative whitespace-nowrap" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                {accent}
                <svg className="absolute -bottom-1.5 left-0 w-full" viewBox="0 0 220 12" preserveAspectRatio="none" aria-hidden="true">
                  <path d="M 4 8 C 60 2, 130 12, 216 4" stroke="#f39325" strokeWidth="3" fill="none" strokeLinecap="round"/>
                </svg>
              </span>
            </>
          )}
          {kicker && <span className="text-brand-navy">{kicker}</span>}
        </h1>
        {subtitle && (
          <p className="mt-6 text-slate-600 text-base md:text-lg leading-relaxed max-w-2xl" data-testid={`${testId || 'page'}-subtitle`}>
            {subtitle}
          </p>
        )}
      </div>
    </section>
  );
}

/** Brand figure — inline SVG accent shape */
export function BrandFigure({ className = "", color = "currentColor", size = 60, opacity = 1 }) {
  return (
    <svg viewBox="0 0 60 90" width={size} height={size * 1.5} className={className} fill="none" style={{ opacity }} aria-hidden="true">
      <circle cx="30" cy="12" r="8" fill={color}/>
      <path d="M30 24 C 44 24, 50 36, 46 54 C 44 66, 38 76, 30 84 C 22 76, 16 66, 14 54 C 10 36, 16 24, 30 24 Z" fill={color}/>
    </svg>
  );
}

/** Final CTA banner — reusable across public pages */
export function FinalCTA({ title, subtitle, primaryLabel, primaryTo, secondaryLabel, secondaryTo }) {
  return (
    <section className="py-20 md:py-24 bg-brand-navy text-white relative overflow-hidden" data-testid="page-final-cta">
      <svg className="absolute -bottom-24 -left-24 w-[400px] h-[400px] pointer-events-none opacity-60" viewBox="0 0 400 400" aria-hidden="true">
        <defs>
          <linearGradient id="ph-cta-orange" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#f39325" stopOpacity="0.20"/>
            <stop offset="100%" stopColor="#f39325" stopOpacity="0"/>
          </linearGradient>
        </defs>
        <circle cx="200" cy="200" r="200" fill="url(#ph-cta-orange)"/>
      </svg>
      <div className="wc-container relative text-center max-w-3xl">
        <div className="inline-flex items-center gap-2.5 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.28em] text-brand-orange">
          <span className="h-px w-8 bg-brand-orange"/>
          We&apos;re Here to Help
          <span className="h-px w-8 bg-brand-orange"/>
        </div>
        <h2 className="mt-5 font-outfit text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1]">{title}</h2>
        {subtitle && <p className="mt-5 text-white/80 text-base md:text-lg leading-relaxed mx-auto max-w-2xl">{subtitle}</p>}
        <div className="mt-9 flex flex-wrap justify-center gap-3">
          <Link to={primaryTo} className="wc-btn-orange !uppercase !tracking-widest text-xs font-bold">
            {primaryLabel}
          </Link>
          {secondaryLabel && (
            <Link to={secondaryTo} className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 hover:bg-white/10 px-7 py-3 text-xs font-bold uppercase tracking-widest text-white" style={{ transition: "background-color 200ms ease" }}>
              {secondaryLabel} <ArrowRight className="w-3.5 h-3.5"/>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
